import { Button, Separator, Typography, Badge } from '@cannect/new-components/atoms'
import { Product } from '@cannect/new-components/molecules'
import { useHistory } from 'react-router'
import { DIGITAL_JOURNEY_ROUTES } from '../constants'
import { useMemo } from 'react'
import { useDigitalJourney } from '@cannect/hooks/useDigitalJourney'
import { DigitalJourney, ProductDetailsDialog } from '@cannect/new-components/organisms'
import { useConfirmOrderSummaryStepMutation } from '@cannect/services/resources/digitalJourney'

export const OrderSummaryStep = () => {
  const { formData } = useDigitalJourney()

  const { mutate: confirmOrderSummary, isPending } = useConfirmOrderSummaryStepMutation()

  const productsList = useMemo(() => {
    return formData?.cart?.products
  }, [formData])

  const summaryFields = useMemo(() => {
    return formData?.cart?.summary
  }, [formData?.cart?.summary])

  const productsQuantity = productsList?.reduce((acc, curr) => acc + curr.product_quantity, 0)

  const deliveryAddressFields = useMemo(() => {
    return formData?.confirm_proof_of_address_document
  }, [formData?.confirm_proof_of_address_document])

  const history = useHistory()

  const onSubmit = async () => {
    if (!formData.common_fields?.digital_journey_id) return

    await confirmOrderSummary(
      {
        digital_journey_id: formData.common_fields?.digital_journey_id
      },
      {
        onSuccess: (response) => {
          if (response?.next_step) {
            history.push(response.next_step)
          }
        }
      }
    )
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <Typography.Heading className="text-center text-primary-700" type="headingSix">
        Certo, aqui está o resumo do seu pedido.
      </Typography.Heading>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 rounded-lg bg-white p-4 shadow-sm sm:p-6">
          <Typography.Heading type="headingSix" className="text-lg font-semibold text-neutral-900">
            Produtos adicionados
          </Typography.Heading>

          <div className="space-y-4">
            {productsList?.map((product) => {
              const isNationalProduct = product?.supplier?.id === 18
              return (
                <div key={product.product_id} className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
                  <div className="flex gap-4">
                    {/* Imagem do produto */}
                    <ProductDetailsDialog productId={product.product_id}>
                      <Product.HeaderImage
                        className="h-20 w-20 flex-shrink-0 cursor-pointer sm:h-24 sm:w-24"
                        src={product?.media?.filter((value) => value.format === 'IMG')?.[0]?.path}
                        alt={product?.name}
                        isNationalProduct={isNationalProduct}
                        product={product}
                      />
                    </ProductDetailsDialog>

                    {/* Informações do produto */}
                    <div className="flex min-w-0 flex-1 flex-col gap-3">
                      {/* Nome e detalhes */}
                      <div>
                        <ProductDetailsDialog productId={product.product_id}>
                          <h3 className="cursor-pointer text-sm font-semibold leading-tight text-neutral-900 hover:text-primary-700 sm:text-base">
                            {product?.name}
                          </h3>
                        </ProductDetailsDialog>
                        {product?.medical_data?.volume && (
                          <p className="text-xs text-neutral-600 sm:text-sm">
                            {product.medical_data.volume}
                            {product?.medical_data?.unit_of_measurement?.toLowerCase()}
                          </p>
                        )}
                        {product?.brand?.name && (
                          <p className="text-xs text-neutral-500 sm:text-sm">{product.brand.name}</p>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2">
                        {product?.class?.name && (
                          <Badge variant="neutral" className="text-xs">
                            {product.class.name}
                          </Badge>
                        )}
                        {product?.flavor && (
                          <Badge className="bg-flavors-default-100 text-xs text-flavors-default-900">
                            {product?.flavor?.name}
                          </Badge>
                        )}
                      </div>

                      {/* Quantidade e preço */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                        <div className="flex items-baseline gap-2 sm:flex-1">
                          <span className="text-sm text-neutral-600">Quantidade:</span>
                          <span className="whitespace-nowrap text-sm font-semibold text-neutral-900">
                            {product.product_quantity} {product.product_quantity > 1 ? 'itens' : 'item'}
                          </span>
                        </div>
                        <div className="flex justify-end sm:min-w-[120px] sm:justify-end">
                          <Product.Price
                            currentPrice={product?.real_price}
                            promotionalPrice={product?.valid_promotion?.promotional_price}
                            className="text-base font-semibold sm:text-lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <Separator />

          {/* Endereço de entrega */}
          <div className="space-y-4">
            <Typography.Heading type="headingSix" className="text-lg font-semibold text-neutral-900">
              Endereço de entrega
            </Typography.Heading>

            <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="col-span-full">
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    Rua
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_street}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    Número
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_number}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    Complemento
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_complement || 'N/A'}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    CEP
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_cep}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    Bairro
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_neighborhood}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    Cidade
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_city}
                  </Typography.Text>
                </div>
                <div>
                  <Typography.Text weight="semibold" type="captionOne" className="text-neutral-600">
                    UF
                  </Typography.Text>
                  <Typography.Text type="paragraphTwo" className="font-medium">
                    {deliveryAddressFields?.address_state}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Resumo do pedido */}
          <div className="space-y-4">
            <Typography.Heading type="headingSix" className="text-lg font-semibold text-neutral-900">
              Resumo do pedido
            </Typography.Heading>
            <DigitalJourney.CartSummary
              summaryFields={summaryFields}
              productsQuantity={productsQuantity}
              productsFields={productsList}
            />
          </div>
        </div>
        <Button
          className="mt-5 font-medium text-primary-700"
          full
          unstyled
          size="lg"
          onClick={() =>
            history.push({
              pathname: DIGITAL_JOURNEY_ROUTES.cart,
              state: { from: DIGITAL_JOURNEY_ROUTES.order_summary }
            })
          }>
          Alterar pedido
        </Button>
        <Button className="mt-1 font-medium" full size="lg" onClick={() => onSubmit()} isLoading={isPending}>
          Ir para o pagamento
        </Button>
      </div>
    </div>
  )
}
